import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { actionsApi } from '../api/actions'
import { ApiError } from '../lib/api'
import { categoryLabel } from '../lib/format'
import { containsDocumentLink, DOC_LINK_IN_NOTE_MESSAGE } from '../lib/contextLinks'
import { Spinner } from './Spinner'

// Ação que o gerente fez por conta própria, fora das sugestões da IA. Vai para a mesma lista
// de ações: a IA lê no próximo relatório, o impacto é medido e, feita, aparece no portal.

const CATEGORIES = ['budget', 'creative', 'targeting', 'bid', 'other'] as const

const SP_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' })
const todaySp = () => SP_DAY.format(new Date())
const daysBack = (n: number) => SP_DAY.format(new Date(Date.now() - n * 86_400_000))

const fieldCls =
  'w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20'

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : 'Erro ao salvar. Tente novamente.')

export function ManagerActionForm({
  reportId,
  portalEnabled,
  onDone,
}: {
  reportId: string
  portalEnabled: boolean
  onDone: () => void
}) {
  const qc = useQueryClient()
  const [description, setDescription] = useState('')
  const [rationale, setRationale] = useState('')
  const [category, setCategory] = useState<string>('')
  const [platform, setPlatform] = useState('')
  const [campaign, setCampaign] = useState('')
  const [done, setDone] = useState(true)
  const [doneOn, setDoneOn] = useState(todaySp())
  const [clientText, setClientText] = useState('')
  const [clientHidden, setClientHidden] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const suggest = useMutation({
    mutationFn: () => actionsApi.suggestClientText({ reportId, description, category: category || 'other', platform: platform || null }),
    onSuccess: (r) => setClientText(r.text),
  })
  const create = useMutation({
    mutationFn: () =>
      actionsApi.createManager({
        reportId,
        description,
        rationale: rationale.trim() || null,
        category,
        platform: platform || null,
        campaign: campaign.trim() || null,
        done,
        doneOn: done ? doneOn : null,
        clientText: clientHidden ? null : clientText.trim() || null,
        clientHidden,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['report', reportId] })
      onDone()
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (containsDocumentLink(description) || containsDocumentLink(rationale)) {
      setLocalError(DOC_LINK_IN_NOTE_MESSAGE)
      return
    }
    setLocalError(null)
    create.mutate()
  }

  const canSubmit = description.trim().length >= 5 && !!category && (!done || !!doneOn) && !create.isPending

  return (
    <form onSubmit={submit} className="space-y-4 rounded-lg border border-sky-200 bg-white p-4 shadow-sm">
      <label className="block">
        <span className="text-sm font-medium text-slate-800">O que você fez</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={600}
          autoFocus
          placeholder="Ex.: Negativei 12 termos de concorrentes na campanha de Pesquisa"
          className={`mt-1 resize-y ${fieldCls}`}
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-slate-800">
          Por quê <span className="font-normal text-slate-400">(opcional)</span>
        </span>
        <textarea
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="Ex.: Gastavam sem trazer contato há três semanas"
          className={`mt-1 resize-y ${fieldCls}`}
        />
      </label>

      <div>
        <span className="text-sm font-medium text-slate-800">Categoria</span>
        <div className="mt-1.5 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Categoria">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={category === c}
              onClick={() => setCategory(c)}
              className={`rounded-full border px-3 py-1 text-sm transition ${
                category === c
                  ? 'border-sky-600 bg-sky-600 text-white'
                  : 'border-slate-300 bg-white text-slate-600 hover:border-sky-400'
              }`}
            >
              {categoryLabel(c)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-[180px_1fr]">
        <label className="block">
          <span className="text-sm font-medium text-slate-800">Plataforma</span>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className={`mt-1 ${fieldCls}`}>
            <option value="">Não informar</option>
            <option value="google_ads">Google Ads</option>
            <option value="meta_ads">Meta Ads</option>
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-800">
            Campanha <span className="font-normal text-slate-400">(opcional, como aparece na conta)</span>
          </span>
          <input value={campaign} onChange={(e) => setCampaign(e.target.value)} maxLength={200} className={`mt-1 ${fieldCls}`} />
        </label>
      </div>

      <fieldset>
        <legend className="text-sm font-medium text-slate-800">Situação</legend>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <label className="inline-flex items-center gap-2">
            <input type="radio" checked={done} onChange={() => setDone(true)} className="text-brand-600 focus:ring-brand-500" />
            Já fiz em
            <input
              type="date"
              value={doneOn}
              min={daysBack(60)}
              max={todaySp()}
              disabled={!done}
              onChange={(e) => setDoneOn(e.target.value)}
              aria-label="Data em que fez"
              className="rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
            />
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="radio" checked={!done} onChange={() => setDone(false)} className="text-brand-600 focus:ring-brand-500" />
            Vou fazer
          </label>
        </div>
      </fieldset>

      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium text-slate-800">Como o cliente vê no portal</span>
          <label className="inline-flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={clientHidden} onChange={(e) => setClientHidden(e.target.checked)} className="rounded text-brand-600 focus:ring-brand-500" />
            Não mostrar ao cliente
          </label>
        </div>
        {!clientHidden && (
          <>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <input
                value={clientText}
                onChange={(e) => setClientText(e.target.value)}
                maxLength={200}
                placeholder="Ex.: Tiramos das buscas termos que não traziam clientes."
                aria-label="Frase para o cliente"
                className={`flex-1 bg-white ${fieldCls}`}
              />
              <button
                type="button"
                disabled={description.trim().length < 5 || suggest.isPending}
                onClick={() => suggest.mutate()}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-violet-300 bg-white px-3 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50"
              >
                {suggest.isPending && <Spinner className="h-4 w-4" />}
                Escrever com IA
              </button>
            </div>
            {suggest.isError && <p className="mt-1 text-xs text-red-600">{errorText(suggest.error)}</p>}
            <p className="mt-1.5 text-xs text-slate-500">
              Uma frase simples, sem termos técnicos. Em branco, a IA escreve sozinha.
              {!done && ' Aparece no portal quando a ação for marcada como feita.'}
              {!portalEnabled && ' O portal deste cliente está desligado: a frase fica pronta para quando ligar.'}
            </p>
          </>
        )}
      </div>

      {(localError || create.isError) && <p className="text-sm text-red-600">{localError ?? errorText(create.error)}</p>}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button type="button" onClick={onDone} className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100">
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {create.isPending && <Spinner className="h-4 w-4 text-white" />}
          Registrar ação
        </button>
      </div>
    </form>
  )
}
